import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { PREVIEW_MESSAGE_SOURCE, READY_TIMEOUT_MS } from "../config";

import { IframePreviewBridge, PreviewMessage, PreviewModuleRect, PreviewStatus } from "../types";

/**
 * Authenticates an inbound preview message. `event.source === iframeWindow` + the per-instance `nonce` are
 * the real check (a sandboxed frame's opaque origin can't be verified by string); `event.origin === "null"`
 * (or "" on engines that report it that way) is defense-in-depth that rejects anything with a real origin.
 */
export const isTrustedPreviewMessage = (
  event: MessageEvent<PreviewMessage>,
  iframeWindow: Window | null,
  nonce: string,
): boolean => {
  if (!iframeWindow || event.source !== iframeWindow) {
    return false;
  }
  if (event.origin !== "null" && event.origin !== "") {
    return false;
  }
  const data = event.data;
  return !!data && data.source === PREVIEW_MESSAGE_SOURCE && data.nonce === nonce;
};

/**
 * Owns the postMessage channel with the preview iframe. Inbound messages are validated by
 * `isTrustedPreviewMessage` (source window + nonce + opaque origin); outbound messages would target `"*"`
 * because an opaque origin has no address.
 */
export const useIframePreviewBridge = (): IframePreviewBridge => {
  const nonceRef = useRef<string>(uuid());
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [modules, setModules] = useState<PreviewModuleRect[]>([]);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [status, setStatus] = useState<PreviewStatus>("loading");
  // Bumped by reset() so the ready-timeout below re-arms for each new iframe document (comment-mode toggle).
  const [generation, setGeneration] = useState<number>(0);

  // contentHeight is deliberately left untouched: a toggle rebuilds the iframe's srcDoc (see
  // usePreviewSrcDoc), and collapsing it to 0 here would drop the panel to INITIAL_IFRAME_HEIGHT on every
  // toggle and then jump back up once the new document reports its real height. That oscillation is what
  // made the outer scroll panel's Windows scrollbar blink (non-overlay scrollbars reflow on every
  // appear/disappear); keeping the last known height until the new document measures itself avoids it.
  const reset = useCallback(() => {
    setModules([]);
    setStatus("loading");
    setGeneration((value) => value + 1);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<PreviewMessage>) => {
      if (
        !isTrustedPreviewMessage(event, iframeRef.current?.contentWindow ?? null, nonceRef.current)
      ) {
        return;
      }

      const data = event.data;
      if (data.type === "ready" || data.type === "modules") {
        setStatus("ready");
      }

      if (data.type === "modules") {
        if (Array.isArray(data.modules)) {
          setModules(data.modules);
        }
        if (typeof data.height === "number" && data.height > 0) {
          setContentHeight(data.height);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Re-armed on mount and on every reset() (a new iframe document). A miss means the measurement script
  // never ran for THIS document (sandbox stripped / script error), so flip a still-pending preview to error
  // after a full READY_TIMEOUT_MS scoped to this document — not the stale first-mount timer.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setStatus((current) => (current === "loading" ? "error" : current));
    }, READY_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [generation]);

  return {
    nonce: nonceRef.current,
    iframeRef,
    modules,
    contentHeight,
    status,
    reset,
  };
};
